import React from 'react';
import { MapPin, Factory, CheckCircle, ArrowRight, Package, Snowflake, Cake, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const FCFoodIndustries = () => {
  const bakeryProducts = [
    "Fresh cakes and pastries",
    "Artisanal breads and buns",
    "Swiss rolls and cupcakes",
    "Premium muffins",
    "Ready-to-eat sandwiches",
    "Custom bakery solutions"
  ];

  const frozenCapabilities = [
    "Advanced freezing technology",
    "Taste and texture preservation",
    "Nutritional quality retention",
    "Extended shelf life solutions",
    "OEM manufacturing services",
    "Custom branded products"
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-orange-600 to-purple-600 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <Factory className="h-12 w-12 text-orange-200" />
              <span className="text-orange-100 font-semibold text-lg">Bakery & Frozen Food Excellence</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">FC Food Industries Pte Ltd</h1>
            <p className="text-xl text-orange-100 max-w-3xl mx-auto">
              Excellence in bakery production and frozen food manufacturing, delivering quality 
              products to retail and institutional customers across Singapore.
            </p>
          </div>
        </div>
      </section>

      {/* Bakery Division Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <Cake className="h-8 w-8 text-blue-500" />
                <span className="text-blue-600 font-semibold text-lg">Bakery Division</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Fresh Bakery Products Daily
              </h2>
              
              <div className="space-y-4 text-gray-600 mb-6">
                <p>
                  Our Bakery produces a wide range of freshly baked products, including cakes, buns, 
                  Swiss rolls, cupcakes, muffins, and sandwiches. Every item is crafted with care using 
                  quality ingredients to ensure consistency, freshness, and great taste.
                </p>
                <p>
                  The bakery supports both retail and institutional requirements, offering customised 
                  solutions to meet diverse client needs. From small batches to large-scale production, 
                  we maintain the highest standards of quality and food safety.
                </p>
              </div>

              {/* Bakery Location */}
              <Card className="mb-6">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <MapPin className="h-4 w-4 text-blue-500" />
                    <span className="font-medium text-gray-700">Bakery Location</span>
                  </div>
                  <p className="text-gray-900">3015 Bedok North Street 5 #06-06 Singapore 486350</p>
                </CardContent>
              </Card>

              <Button className="bg-blue-600 hover:bg-blue-700">
                <Link to="/contact" className="flex items-center">
                  Inquire About Bakery Products
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1698792991083-24f5cef31018"
                alt="Artisanal bakery production"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Bakery Products */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Bakery Products
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Wide variety of freshly baked goods crafted daily with quality ingredients 
              to serve retail and institutional customers.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Products List */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Product Range</h3>
              <div className="space-y-3">
                {bakeryProducts.map((product, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{product}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Product Images */}
            <div className="space-y-4">
              <img 
                src="https://images.unsplash.com/photo-1732565674273-44e3ba2823bc"
                alt="Fresh artisanal bread"
                className="w-full h-64 object-cover rounded-lg shadow-lg"
              />
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1599819055803-717bba43890f"
                  alt="Packaged bakery products"
                  className="w-full h-48 object-cover rounded-lg shadow-lg"
                />
                <img 
                  src="https://images.unsplash.com/photo-1703607888337-aae6d77b3d83"
                  alt="Bakery production line"
                  className="w-full h-48 object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Frozen Food Factory Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://customer-assets.emergentagent.com/job_teng-corporate/artifacts/sac0mxby_Screenshot%202025-11-05%20125058.png"
                alt="Frozen food products"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="flex items-center space-x-3 mb-6">
                <Snowflake className="h-8 w-8 text-red-500" />
                <span className="text-red-600 font-semibold text-lg">Frozen Food Manufacturing</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Advanced Frozen Food Factory
              </h2>
              
              <div className="space-y-4 text-gray-600 mb-6">
                <p>
                  Our Frozen Food Factory provides operational resilience and reliable production 
                  capacity for both in-house and client needs. Equipped with advanced freezing and 
                  packaging technology, the facility preserves taste, texture, and nutritional quality 
                  while ensuring long shelf life.
                </p>
                <p>
                  In addition to supporting institutional and commercial food supply, the factory 
                  offers Original Equipment Manufacturing (OEM) services, enabling clients to develop 
                  and produce their own branded frozen food products with the same high standards of 
                  quality and safety that First Cuisine is known for.
                </p>
              </div>

              {/* Frozen Food Location */}
              <Card className="mb-6">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <MapPin className="h-4 w-4 text-red-500" />
                    <span className="font-medium text-gray-700">Factory Location</span>
                  </div>
                  <p className="text-gray-900">13 Tuas Bay Walk Singapore 637759</p>
                </CardContent>
              </Card>

              <Button className="bg-red-600 hover:bg-red-700">
                <Link to="/oem-services" className="flex items-center">
                  Explore OEM Services
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Frozen Food Capabilities */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Frozen Food Capabilities
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              State-of-the-art technology and expertise to deliver exceptional frozen food 
              products while maintaining quality, safety, and nutritional value.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {frozenCapabilities.map((capability, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{capability}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Combined Operations Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Integrated Food Manufacturing Solutions
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our dual operations in bakery and frozen food manufacturing provide 
              comprehensive solutions for diverse client needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex justify-center mb-4">
                  <Cake className="h-8 w-8 text-blue-500" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Fresh Bakery</h4>
                <p className="text-sm text-gray-600">
                  Daily production of quality baked goods
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex justify-center mb-4">
                  <Snowflake className="h-8 w-8 text-red-500" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Advanced Freezing</h4>
                <p className="text-sm text-gray-600">
                  Optimal preservation technology
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex justify-center mb-4">
                  <Package className="h-8 w-8 text-blue-500" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">OEM Services</h4>
                <p className="text-sm text-gray-600">
                  Custom branded product development
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex justify-center mb-4">
                  <Award className="h-8 w-8 text-red-500" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Quality Assured</h4>
                <p className="text-sm text-gray-600">
                  Consistent standards across operations
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Partner with FC Food Industries
          </h2>
          <p className="text-xl mb-8 text-orange-100 max-w-3xl mx-auto">
            Whether you need fresh bakery products or frozen food manufacturing solutions, 
            we're ready to support your business with quality and reliability.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100">
              <Link to="/contact" className="flex items-center">
                Get in Touch
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-orange-600">
              <Link to="/oem-services">Explore OEM Services</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FCFoodIndustries;
